import clsx from "clsx";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  TreeItem2Content,
  TreeItem2IconContainer,
  TreeItem2Root,
  TreeItem2GroupTransition,
} from "@mui/x-tree-view/TreeItem2";
import type { UseTreeItem2Parameters } from "@mui/x-tree-view/useTreeItem2";
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2";
import { TreeItem2Provider } from "@mui/x-tree-view/TreeItem2Provider";
import { TreeItem2Icon } from "@mui/x-tree-view/TreeItem2Icon";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import type { ElementType, HTMLAttributes, MouseEventHandler, Ref } from "react";
import { forwardRef } from "react";

interface StyledTreeItemProps extends Omit<UseTreeItem2Parameters, "rootRef">, HTMLAttributes<HTMLLIElement> {
  bgColor?: string;
  bgColorForDarkMode?: string;
  color?: string;
  colorForDarkMode?: string;
  labelIcon: ElementType<any>;
  labelStatus?: { issue: string; id: string }[];
  onIconClick?: MouseEventHandler<HTMLDivElement>;
}

const CustomTreeItemRoot = styled(TreeItem2Root)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const CustomTreeItemIconContainer = styled(TreeItem2IconContainer)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const CustomTreeItemGroupTransition = styled(TreeItem2GroupTransition)(({ theme }) => ({
  marginLeft: 0,
  [`& .content`]: {
    paddingLeft: theme.spacing(2),
  },
}));

export const CustomTreeItem = forwardRef(function CustomTreeItem(props: StyledTreeItemProps, ref: Ref<HTMLLIElement>) {
  const theme = useTheme();
  const {
    id,
    itemId,
    label,
    disabled,
    children,
    bgColor,
    color,
    labelIcon: LabelIcon,
    labelStatus,
    colorForDarkMode,
    bgColorForDarkMode,
    onClick,
    onIconClick,
    ...other
  } = props;

  const { getRootProps, getContentProps, getIconContainerProps, getLabelProps, getGroupTransitionProps, status } =
    useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  const style = {
    "--tree-view-color": theme.palette.mode !== "dark" ? color : colorForDarkMode,
    "--tree-view-bg-color": theme.palette.mode !== "dark" ? bgColor : bgColorForDarkMode,
  };

  return (
    <TreeItem2Provider itemId={itemId}>
      <CustomTreeItemRoot {...getRootProps({ ...other, style })}>
        <TreeItem2Content
          sx={{
            marginBottom: theme.spacing(0.3),
            color: theme.palette.text.primary,
            borderRadius: theme.spacing(1),
            px: 2,
            fontWeight: theme.typography.fontWeightMedium,
            textTransform: "capitalize",
            "&.expanded": {
              fontWeight: theme.typography.fontWeightRegular,
            },
            "&.expanded&:hover": {
              // backgroundColor: darken(theme.palette.action.selected, 100),
            },
            "&:hover": {
              color: theme.palette.primary.main,
            },

            // Custom styling for selected state
            "&.selected, &.selected.focused, &[data-selected='true']": {
              "&:hover": {
                backgroundColor: theme.palette.action.selected,
              },
              backgroundColor: theme.palette.action.selected,
              color: theme.palette.primary.main,
              ".MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          }}
          {...getContentProps({
            className: clsx("content", {
              expanded: status.expanded,
              selected: status.selected,
              focused: status.focused,
            }),
            onClick: onClick,
          })}
        >
          <Box
            sx={{
              display: "flex",
              flexGrow: 1,
              alignItems: "center",
              py: 0.5,
              pr: 0,
            }}
          >
            <Box component={LabelIcon} color="inherit" sx={{ mr: 1 }} />
            <Typography
              {...getLabelProps({
                variant: "body2",
                sx: { display: "flex", fontWeight: "inherit", flexGrow: 1 },
              })}
            >
              {label}
            </Typography>
            {labelStatus && labelStatus.length > 0 && (
              <Typography variant="caption" color="error" style={{ display: "flex", alignItems: "center" }}>
                <ErrorOutlineOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} color={"error"} />
              </Typography>
            )}
          </Box>
          <CustomTreeItemIconContainer {...getIconContainerProps()} onClick={onIconClick}>
            <TreeItem2Icon status={status} />
          </CustomTreeItemIconContainer>
        </TreeItem2Content>
        {children && (
          <CustomTreeItemGroupTransition {...getGroupTransitionProps()}>{children}</CustomTreeItemGroupTransition>
        )}
      </CustomTreeItemRoot>
    </TreeItem2Provider>
  );
});
